import type { ReactNode } from 'react'
import { useId } from 'react'

export interface FieldProps {
  label?: ReactNode
  /** Keep the label for screen readers but not on screen — dense editor rows. */
  hideLabel?: boolean
  hint?: ReactNode
  error?: ReactNode
  className?: string
  /** Called with the ids the control must wire up. */
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode
}

/**
 * Label + hint + error wrapper shared by every text-ish control, so the a11y wiring
 * exists in exactly one place.
 */
export function Field({ label, hideLabel, hint, error, className, children }: FieldProps) {
  const base = useId()
  const id = `${base}-input`
  const hintId = hint ? `${base}-hint` : undefined
  const errorId = error ? `${base}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className={`rs-field ${className ?? ''}`}>
      {label ? (
        <label className={hideLabel ? 'rs-visually-hidden' : 'rs-field__label'} htmlFor={id}>
          {label}
        </label>
      ) : null}
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint ? (
        <span className="rs-field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="rs-field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  )
}
